import React from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import * as Dati from "../../../Dati";
import * as DatiFide from "../../../DatiFide";
import * as Glob from "../../../Globale";
import { Trad } from "../../../components/";
import { _Autocomplete, _DateField, _NumberField, _Select } from "../../form";
import { _ExposureLimitApprovals, _ExposureLimitCharts } from "..";
import moment from "moment";

interface IExposureLimitFormContentProps {
    Data: DatiFide.CDatiFideAffidamento;
    CurrentStatus: string;
    Limits: Dati.Dictionary<number>;
    Types: { [T: string]: Dati.TDictTipo };
    OnChange: (e: React.FormEvent<any> | string, val?: unknown) => void;
    OnChangeDerogation: (e: React.FormEvent<any>, tipo: string, verso: string) => void;
}

const useStyles = makeStyles(({ spacing }: Theme) => ({
    subtitle: {
        width: "100%",
        paddingTop: spacing(1),
        paddingLeft: spacing(2)
    }
}));

export const _ExposureLimitFormContent: React.FC<IExposureLimitFormContentProps> = ({ Data, CurrentStatus, Limits, Types, OnChange, OnChangeDerogation }) => {
    const classes = useStyles();
    const isReviewed = CurrentStatus == Glob.Tipi_FAE_Sottoscritto || CurrentStatus == Glob.Tipi_FAE_Revisione;
    const isTypeEditable = Data.ID_TipoFideAffidamento != Glob.Tipi_FA_Affidamento;
    const OnChangeSigningDate = (e: string, val: string | Date) => {
        if (typeof val != "string") {
            const ExpiryDate: Date = new Date();

            ExpiryDate.setDate(val.getDate());
            ExpiryDate.setMonth(val.getMonth());
            ExpiryDate.setFullYear(val.getFullYear() + 1);

            OnChange("ScadenzaAffidamento", ExpiryDate);
        }
        OnChange(e, val);
    };

    const OnChangeType = (e: React.FormEvent<any>) => {
        if (e.currentTarget.value == Glob.Tipi_FA_NonProvvisorie) {
            OnChange("ID_TipoSOA", "");
        }
        OnChange(e);
    };

    return (
        <>
            <Grid container spacing={4}>
                {isTypeEditable && (
                    <Grid item xs={12} md={2}>
                        <FormControl component="fieldset">
                            <RadioGroup row aria-label="ID_TipoFideAffidamento" name="ID_TipoFideAffidamento" value={Data.ID_TipoFideAffidamento} onChange={OnChangeType}>
                                <FormControlLabel value={Glob.Tipi_FA_Provvisorie} control={<Radio />} label={Trad.Trad("Provvisorie")} />
                                <FormControlLabel value={Glob.Tipi_FA_NonProvvisorie} control={<Radio />} label={Trad.Trad("Non provvisorie")} />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                )}
                <Grid item container xs={12} md={isTypeEditable ? 10 : 12}>
                    <_ExposureLimitCharts ID_FideAffidamento={Data.ID_FideAffidamento} ID_TipoFideAffidamento={Data.ID_TipoFideAffidamento} />
                </Grid>
                <Grid item xs={12} md={2}>
                    <TextField fullWidth id="ID_FideAffidamento" label={Trad.Trad("ID")} value={Data.ID_FideAffidamento} disabled />
                </Grid>
                <Grid item xs={12} md={4}>
                    <_Autocomplete Value={Data.ID_AnagDa ?? ""} Name="ID_AnagDa" Label="Affidatario" OnChange={OnChange} Url="/api/Anag/RicercaVeloce?id_TipoAnag=&ricerca=" Disabled={isReviewed} />
                </Grid>
                <Grid item xs={12} md={4}>
                    <_Autocomplete Value={Data.ID_AnagA ?? ""} Name="ID_AnagA" Label="Affidato" Url="/api/Anag/RicercaVeloce?id_TipoAnag=&ricerca=" OnChange={OnChange} Disabled />
                </Grid>
                <Grid item xs={12} md={3}>
                    <_Select
                        Value={Data.ID_TipoFideAffidamentoEsito || Glob.Tipi_FAE_CreazioneAffidamento}
                        Name="ID_TipoFideAffidamentoEsito"
                        Label="Stato"
                        Disabled={CurrentStatus == Glob.Tipi_FAE_Sottoscritto}
                        OnChange={OnChange}
                        Options={Types.FideAffidamentoEsito ?? {}}
                        Permissions
                    />
                </Grid>
                {Data.ID_TipoFideAffidamento != Glob.Tipi_FA_NonProvvisorie && (
                    <Grid item xs={12} md={2}>
                        <_Select Value={Data.ID_TipoSOA} Name="ID_TipoSOA" Label="SOA" Disabled={isReviewed} OnChange={OnChange} Options={Types.TipiSOA ?? {}} TranslationsOff />
                    </Grid>
                )}
                {Glob.AzioneAbilitata("FideAffidamentoDatiPOS", Glob.Lettura) && (
                    <>
                        <Grid item xs={12} md={2}>
                            <_DateField Value={Data.Data} Name="Data" Label="Data sottoscrizione" OnChange={OnChangeSigningDate} Disabled={isReviewed} required={false} />
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <_DateField Value={Data.ScadenzaAffidamento} Name="ScadenzaAffidamento" Label="Data scadenza" OnChange={OnChange} Disabled={isReviewed} required={false} />
                        </Grid>
                    </>
                )}
                {Data.ID_TipoFideAffidamento == Glob.Tipi_FA_Affidamento && (
                    <>
                        <div className={classes.subtitle}>
                            <Typography variant="subtitle2">{Trad.Trad("FA-Provvisorie")}</Typography>
                        </div>
                        <Grid item xs={12} md={4}>
                            <_NumberField
                                ProgressBar
                                Id="LimiteMassimoContraente_Provvisorie"
                                Label="Limite per Contraente / Importo Affidamento"
                                Value={Data.LimiteMassimoContraente_Provvisorie}
                                Max={Limits["LimiteMassimoContraente_ProvvisorieMAX"]}
                                OnChange={OnChange}
                                OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <_NumberField
                                ProgressBar
                                Id="LimiteMassimoFideiussione_Provvisorie"
                                Label="Lim. Fideiussione"
                                Value={Data.LimiteMassimoFideiussione_Provvisorie}
                                Max={Limits["LimiteMassimoFideiussione_ProvvisorieMAX"]}
                                OnChange={OnChange}
                                OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                            />
                        </Grid>
                        {Glob.AzioneAbilitata("FideAffidamentoDatiPOS", Glob.Lettura) && (
                            <Grid item xs={12} md={4}>
                                <_NumberField
                                    ProgressBar
                                    Id="LimiteMassimoFideiussioneAutonomiaPOS_Provvisorie"
                                    Label="Lim. Fideiussione POS"
                                    Value={Data.LimiteMassimoFideiussioneAutonomiaPOS_Provvisorie}
                                    Max={Limits["LimiteMassimoFideiussioneAutonomiaPOS_ProvvisorieMAX"]}
                                    OnChange={OnChange}
                                    OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                                />
                            </Grid>
                        )}
                        <div className={classes.subtitle}>
                            <Typography variant="subtitle2">{Trad.Trad("FA-NonProvvisorie")}</Typography>
                        </div>
                        <Grid item xs={12} md={4}>
                            <_NumberField
                                ProgressBar
                                Id="LimiteMassimoContraente_NonProvvisorie"
                                Label="Limite per Contraente / Importo Affidamento"
                                Value={Data.LimiteMassimoContraente_NonProvvisorie}
                                Max={Limits["LimiteMassimoContraente_NonProvvisorieMAX"]}
                                OnChange={OnChange}
                                OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <_NumberField
                                ProgressBar
                                Id="LimiteMassimoFideiussione_NonProvvisorie"
                                Label="Lim. Fideiussione"
                                Value={Data.LimiteMassimoFideiussione_NonProvvisorie}
                                Max={Limits["LimiteMassimoFideiussione_NonProvvisorieMAX"]}
                                OnChange={OnChange}
                                OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                            />
                        </Grid>
                        {Glob.AzioneAbilitata("FideAffidamentoDatiPOS", Glob.Lettura) && (
                            <Grid item xs={12} md={4}>
                                <_NumberField
                                    ProgressBar
                                    Id="LimiteMassimoFideiussioneAutonomiaPOS_NonProvvisorie"
                                    Label="Lim. Fideiussione POS"
                                    Value={Data.LimiteMassimoFideiussioneAutonomiaPOS_NonProvvisorie}
                                    Max={Limits["LimiteMassimoFideiussioneAutonomiaPOSMAX_NonProvvisorie"]}
                                    OnChange={OnChange}
                                    OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                                />
                            </Grid>
                        )}
                    </>
                )}
                {Data.ID_TipoFideAffidamento != Glob.Tipi_FA_Affidamento && (
                    <Grid item container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <_NumberField
                                ProgressBar
                                Id="LimiteMassimoContraente"
                                Label="Limite per Contraente / Importo Affidamento"
                                Value={Data.LimiteMassimoContraente}
                                Max={Limits["LimiteMassimoContraenteMAX"]}
                                OnChange={OnChange}
                                OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <_NumberField
                                ProgressBar
                                Id="LimiteMassimoFideiussione"
                                Label="Lim. Fideiussione"
                                Value={Data.LimiteMassimoFideiussione}
                                Max={Limits["LimiteMassimoFideiussioneMAX"]}
                                OnChange={OnChange}
                                OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                            />
                        </Grid>
                        {Glob.AzioneAbilitata("FideAffidamentoDatiPOS", Glob.Lettura) && (
                            <Grid item xs={12} md={4}>
                                <_NumberField
                                    ProgressBar
                                    Id="LimiteMassimoFideiussioneAutonomiaPOS"
                                    Label="Lim. Fideiussione POS"
                                    Value={Data.LimiteMassimoFideiussioneAutonomiaPOS}
                                    Max={Limits["LimiteMassimoFideiussioneAutonomiaPOSMAX"]}
                                    OnChange={OnChange}
                                    OnChangeDerogation={e => OnChangeDerogation(e, "Importo", ">")}
                                />
                            </Grid>
                        )}
                    </Grid>
                )}
                <Grid item xs={12}>
                    <_Autocomplete
                        Value={Data.ListaBinderJSON}
                        Name="ListaBinderJSON"
                        Label="ListaBinder"
                        OnChange={OnChange}
                        Url={`/api/FideAffidamento/ListaBinderDisponibili?id_AnagDa=${Data.ID_AnagDa}&dateRelease=${moment(Data.Data).utc().format()}`}
                        Disabled={isReviewed}
                        Multiple
                        SearchInput
                        TranslationsOff
                    />
                </Grid>
                <Grid item xs={12}>
                    <TextField multiline fullWidth variant="outlined" id="Note" label={Trad.Trad("Commenti")} rows={3} rowsMax={5} value={Data.Note || ""} onChange={OnChange} />
                </Grid>
            </Grid>
            {Data.ListaFideAffidamentoApprovazioneJSON && (
                <_ExposureLimitApprovals
                    Name="ListaFideAffidamentoApprovazioneJSON"
                    Data={Data.ListaFideAffidamentoApprovazioneJSON}
                    Types={Types["FideAffidamentoApprovazione"]}
                    OnChange={OnChange}
                />
            )}
        </>
    );
};



// WEBPACK FOOTER //
// ./src/inar2/components/ExposureLimit/ExposureLimitFormContent.tsx